import { ChevronRight, Handshake } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { ProgressBar } from "@/components/progress-bar"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ContractStatusBadge } from "@/features/contracts/components/contract-status-badge"
import { allocatedToOTMt } from "@/features/contracts/lib/contract-progress"
import { formatDate, formatNumber, percent } from "@/lib/format"
import { progressTone } from "@/lib/progress"
import { paths } from "@/routes/paths"
import type { Contract } from "@/types/domain"

const headCellClass =
  "h-11 px-4 text-muted-foreground text-xs font-medium uppercase tracking-wide"
const bodyCellClass = "px-4 py-4"

interface ContractsTableProps {
  contracts: Contract[]
}

export function ContractsTable({ contracts }: ContractsTableProps) {
  const { t, i18n } = useTranslation(["contracts", "common"])
  const navigate = useNavigate()

  if (contracts.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Handshake className="size-8" />}
          title={t("table.empty")}
          description={t("subtitle")}
          className="border-0 py-16"
          action={
            <Button asChild size="sm">
              <Link to={paths.contractNew}>{t("newContract")}</Link>
            </Button>
          }
        />
      </Card>
    )
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead className={headCellClass}>{t("table.contractId")}</TableHead>
              <TableHead className={headCellClass}>{t("table.client")}</TableHead>
              <TableHead className={headCellClass}>{t("table.product")}</TableHead>
              <TableHead className={`${headCellClass} text-right`}>
                {t("table.quantity")}
              </TableHead>
              <TableHead className={`${headCellClass} min-w-[200px]`}>
                {t("tableExtras.delivery")}
              </TableHead>
              <TableHead className={`${headCellClass} min-w-[140px]`}>
                {t("table.status")}
              </TableHead>
              <TableHead className={`${headCellClass} text-right`}>
                {t("tableExtras.ordersCount")}
              </TableHead>
              <TableHead className={headCellClass}>
                {t("table.signatureDate")}
              </TableHead>
              <TableHead aria-hidden className={`${headCellClass} w-8`} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {contracts.map((c) => {
              const totalQty = c.quantity ?? 0
              const shippedMt = allocatedToOTMt(c)
              const pct = percent(shippedMt, totalQty)
              const ordersCount = (c.Orders ?? []).length
              return (
                <TableRow
                  key={String(c.id)}
                  className="hover:bg-muted/60 cursor-pointer"
                  onClick={() => navigate(paths.contractDetail(c.id))}
                >
                  <TableCell className={`${bodyCellClass} font-medium`}>
                    <div>{c.contractNumber ?? `#${c.id}`}</div>
                    {c.created_at ? (
                      <div className="text-muted-foreground text-xs">
                        {formatDate(c.created_at, i18n.language)}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className={`${bodyCellClass} max-w-[220px] truncate`}>
                    {c.clientName}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    {c.productType
                      ? t(`options.productTypes.${c.productType}`, {
                          defaultValue: c.productType,
                        })
                      : ""}
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} text-right tabular-nums`}
                  >
                    {formatNumber(totalQty)} {t("units.mt", { ns: "common" })}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="tabular-nums">
                          {formatNumber(shippedMt)} /{" "}
                          {formatNumber(totalQty)}{" "}
                          {t("units.mt", { ns: "common" })}
                        </span>
                        <span className="text-muted-foreground tabular-nums">
                          {formatNumber(pct)}%
                        </span>
                      </div>
                      <ProgressBar value={pct} tone={progressTone(pct)} />
                    </div>
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <ContractStatusBadge status={c.status} />
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} text-right tabular-nums`}
                  >
                    {ordersCount}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    {formatDate(c.contractDate, i18n.language) || "—"}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <ChevronRight className="text-muted-foreground size-4" />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  )
}
